'use client';

import { Box } from '@mui/material';
import { LeftArrowOutlined, RightArrowOutlined } from '@zeelool/icon';
import Image from '@/components/Image';
import { useEffect, useRef } from 'react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

interface PhotoWallProps {
  pageData: any;
}

export default function PhotoWall({ pageData }: PhotoWallProps) {
  const swiperRef = useRef<any>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const list = pageData?.photo_wall?.list || [];
  const blackText = pageData?.photo_wall?.black_text || '';
  const whiteText = pageData?.photo_wall?.white_text || '';

  useEffect(() => {
    // 设置视频观察器
    const observeVideos = () => {
      setTimeout(() => {
        const options = {
          threshold: 0.8,
        };
        observerRef.current = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const video = entry.target as HTMLVideoElement;
            if (entry.isIntersecting) {
              video.paused && video.play();
            } else {
              !video.paused && video.pause();
            }
          });
        }, options);

        const slides = document.querySelectorAll('.video-slide');
        slides.forEach((slide) => {
          observerRef.current?.observe(slide);
        });
      }, 2000);
    };

    observeVideos();

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  const stopPlay = () => {
    swiperRef.current?.swiper?.autoplay?.stop();
  };

  const startPlay = () => {
    if (swiperRef.current && list.length > 3) {
      swiperRef.current.swiper.autoplay.start();
    }
  };

  // 暴露给父组件使用
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).photoWallControls = {
        stopPlay,
        startPlay,
      };
    }
  }, [list.length]);

  // 只在PC端应用动画
  useEffect(() => {
    if (pageData && Object.keys(pageData).length > 0) {
      // 延迟初始化，确保DOM完全渲染
      const initTimer = setTimeout(() => {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                // 触发标题动画
                const title = document.querySelector('.photo-list.pc-version .title');
                if (title) {
                  title.classList.remove('opacity0');
                  title.classList.add('fadeInUpAnimate');
                }

                // 触发anchor动画
                const anchor = document.querySelector('.photo_wrapper_anchor');
                if (anchor) {
                  anchor.classList.remove('opacity0');
                  anchor.classList.add('fadeInUpAnimate');
                }

                // 触发初始可见slide的动画
                const visibleSlides = document.querySelectorAll(
                  '.photo-list.pc-version .swiper-slide-visible .photo-item',
                );
                visibleSlides.forEach((item, index) => {
                  setTimeout(
                    () => {
                      item.classList.remove('opacity0');
                      item.classList.add('fadeInRightPhotoWallAnimate');
                    },
                    100 * (index + 1),
                  );
                });

                // 断开观察器
                observer.disconnect();
              }
            });
          },
          { threshold: 0.1 },
        );

        // 只观察PC端的photo-list元素
        const photoWallElement = document.querySelector('.photo-list.pc-version');
        if (photoWallElement) {
          observer.observe(photoWallElement);
        } else {
          // 如果找不到PC端元素，直接触发动画
          setTimeout(() => {
            const photoWallTitle = document.querySelector('.photo-list.pc-version .title');
            const photoItems = document.querySelectorAll('.photo-list.pc-version .photo-item');

            if (photoWallTitle) {
              photoWallTitle.classList.add('fadeInUpAnimate');
            }

            photoItems.forEach((item, index) => {
              setTimeout(
                () => {
                  item.classList.add('fadeInRightPhotoWallAnimate');
                },
                100 * (index + 1),
              );
            });
          }, 2000);
        }

        return () => observer.disconnect();
      }, 1500);

      return () => clearTimeout(initTimer);
    }
  }, [pageData]);

  const isEmpty = (obj: any) => !obj || Object.keys(obj).length === 0;

  if (isEmpty(pageData) || isEmpty(list)) return null;

  // PC端配置
  const pcSwiperOptions = {
    modules: [Autoplay, Navigation, Pagination],
    spaceBetween: 120,
    slidesPerView: 3.5,
    slidesPerGroup: 1,
    loop: true,
    autoplay:
      list.length > 3
        ? {
            delay: 3000,
            stopOnLastSlide: false,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false,
    navigation:
      list.length > 3
        ? {
            nextEl: '.photo-next',
            prevEl: '.photo-prev',
          }
        : false,
    // 添加Swiper事件监听
    on: {
      slideChange() {
        // 当slide改变时，为新的可见slide添加动画
        setTimeout(() => {
          const visibleSlides = document.querySelectorAll(
            '.photo-list.pc-version .swiper-slide-visible .photo-item',
          );
          visibleSlides.forEach((slide) => {
            if (!slide.classList.contains('fadeInRightPhotoWallAnimate')) {
              slide.classList.add('fadeInRightPhotoWallAnimate');
            }
          });
        }, 100);
      },
    },
  };

  // 移动端配置
  const mobileSwiperOptions = {
    modules: [Autoplay],
    spaceBetween: 40,
    slidesPerView: 1.5,
    slidesPerGroup: 1,
    loop: list.length > 1,
    slidesOffsetBefore: 14,
    slidesOffsetAfter: 14,
    autoplay:
      list.length > 1
        ? {
            delay: 3000,
            stopOnLastSlide: false,
            disableOnInteraction: false,
          }
        : false,
  };

  return (
    <>
      {/* 移动端布局 - 不使用动画 */}
      <div className="block md:hidden photo-list mobile-version w-full bg-[#0f0f0f] py-[45px] pb-[38px] border-b border-white">
        <p className="title text-left text-[65px] pl-[26px] leading-[60px]">
          <span
            className="black_text block font-bb text-transparent"
            style={
              {
                WebkitTextStroke: '1px #FFFFFF',
              } satisfies React.CSSProperties
            }
          >
            {blackText}
          </span>
          <span className="white_text block font-bb text-white font-bold">{whiteText}</span>
        </p>

        <Box
          className="pt-[35.5px]"
          sx={{
            '.swiper-wrapper': {
              padding: '10px 14px',
            },
          }}
        >
          <Swiper ref={swiperRef} {...mobileSwiperOptions} className="photo-swiper">
            {list.map((item: any, index: number) => (
              <SwiperSlide key={item.link || `mobile-slide-${index}`} className="photo-item">
                {item.type === '1' ? (
                  <Image
                    src={item.link}
                    alt={`Zeelool optical eyeglasses online store candy-glasses-photo-wall${index + 1}`}
                    width={350}
                    height={350}
                    className={`w-full h-auto ${index % 2 === 0 ? 'rotate_left' : 'rotate_right'}`}
                  />
                ) : (
                  <div
                    className={`${index % 2 === 0 ? 'rotate_left' : 'rotate_right'} video-slide-out relative overflow-hidden`}
                  >
                    <video
                      className="video-slide w-full h-auto"
                      poster={item.poster_img}
                      loop
                      muted
                      playsInline
                    >
                      <source src={item.link} />
                    </video>
                  </div>
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      </div>

      {/* PC端布局 - 使用动画 */}
      <div className="hidden md:block photo-list pc-version relative w-screen bg-[#0f0f0f] py-[100px] md:py-[120px] xl:py-[150px]">
        <p className="title opacity0 text-center text-[80px] md:text-[120px] xl:text-[150px]">
          <span
            className="black_text font-bb text-transparent leading-[60px]"
            style={
              {
                WebkitTextStroke: '2px #FFFFFF',
              } satisfies React.CSSProperties
            }
          >
            {blackText}
          </span>
          <span className="white_text font-bb text-white">{whiteText}</span>
        </p>

        <div className="photo_wrapper_anchor opacity0 absolute top-[200px] md:top-[280px] xl:top-[360px] left-0 h-[200px] md:h-[260px] xl:h-[324px] w-[1px]" />

        <div>
          <Box
            sx={{
              '.swiper-wrapper': {
                padding: '10px 100px',
              },
            }}
          >
            <Swiper ref={swiperRef} {...pcSwiperOptions} className="photo-swiper">
              {list.map((item: any, index: number) => (
                <SwiperSlide key={item.link || `pc-slide-${index}`}>
                  <div className="photo-item pt-[60px] md:pt-[80px] xl:pt-[100px]">
                    {item.type === '1' ? (
                      <Image
                        src={item.link}
                        alt={`Zeelool optical eyeglasses online store candy-glasses-photo-wall${index + 1}`}
                        width={480}
                        height={480}
                        className={`w-full h-auto ${index % 2 === 0 ? 'rotate_left' : 'rotate_right'}`}
                      />
                    ) : (
                      <div
                        className={`${index % 2 === 0 ? 'rotate_left' : 'rotate_right'} video-slide-out relative overflow-hidden`}
                      >
                        <video
                          className="video-slide w-full h-auto"
                          poster={item.poster_img}
                          loop
                          muted
                          playsInline
                        >
                          <source src={item.link} />
                        </video>
                      </div>
                    )}
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
          {list.length > 3 && (
            <>
              <div className="photo-prev absolute right-[90px] md:right-[110px] xl:right-[130px] bottom-[30px] md:bottom-[50px] xl:bottom-[80px] z-10 cursor-pointer w-[40px] h-[40px] bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-40 transition-all">
                <LeftArrowOutlined className="text-[#282828] text-[18px]" />
              </div>
              <div className="photo-next absolute right-[20px] md:right-[40px] xl:right-[60px] bottom-[30px] md:bottom-[50px] xl:bottom-[80px] z-10 cursor-pointer w-[40px] h-[40px] bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-40 transition-all">
                <RightArrowOutlined className="text-[#282828] text-[18px]" />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
